import {
  AccountState as LicenseState,
  LicenseCode,
} from '@account-users/account/models';
import { AccountService } from '@account-users/account/services/account.service';
import { UserService } from '@account-users/account/../user/services/user.service';
import { AuthService } from '@account-users/auth/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, forkJoin, Observable, iif, of, throwError, merge } from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  map,
  mergeMap,
  repeatWhen,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { FeatureToggleService } from '../../services/feature-toggle.service';

import { LicenseActions } from './actions';

@Injectable()
export class LicenseEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly accountService: AccountService,
    private readonly authService: AuthService,
    private readonly featureToggleService: FeatureToggleService,
    private readonly toastrService: ToastrService,
    private readonly userService: UserService,
  ) {}

  loadLicenses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LicenseActions.GetLicenses.request),
      switchMap(({ accountId, filters }) =>
        forkJoin([
          this.userService.fetchUser(this.authService.profile?.userId),
          this.accountService.getAccountLicenses(accountId, filters),
          this.featureToggleService.getLicenses(),
        ]).pipe(
          map(([user, bootstrappedLicenses, allLicenses]) => {
            const { roles } = user['user'];
            const lookup = bootstrappedLicenses?.reduce((a, b) => {
              a[b.licenseCode] = b;
              return a;
            }, {});
            return LicenseActions.GetLicenses.success({
              items: allLicenses.map((l) => ({
                ...l,
                enabled: !!lookup[l.code],
                state: lookup[l.code]?.state,
                disabled:
                  l.readOnly ||
                  !(roles || []).filter((r: string) => l.managedBy.includes(r))
                    ?.length,
              })),
            });
          }),
          catchError((err: HttpErrorResponse) => {
            this.toastrService.error(err?.message);
            return of(LicenseActions.failure({ errorMessage: err?.message }));
          }),
        ),
      ),
    ),
  );

  addLicense$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LicenseActions.AddLicense.request),
      mergeMap(({ accountId, licenseCode }) =>
        this.accountService.putAccountLicense(accountId, licenseCode).pipe(
          switchMap(() => {
            let stopRepeating = false;
            return this.accountService
              .getAccountLicenseState(accountId, licenseCode)
              .pipe(
                mergeMap((license) => {
                  const { state } = license as any;
                  stopRepeating = state !== LicenseState.Pending;
                  return stopRepeating ? of(license) : EMPTY;
                }),
                repeatWhen((license) =>
                  license.pipe(
                    takeWhile(() => !stopRepeating),
                    delay(1000),
                  ),
                ),
                map((response) =>
                  LicenseActions.AddLicense.success({
                    update: {
                      id: (response as any)?.licenseCode,
                      changes: { state: (response as any)?.state },
                    },
                  }),
                ),
                catchError((err) => throwError(() => err)),
              );
          }),
          catchError((err) => {
            this.toastrService.error(err);
            return of(LicenseActions.failure({ errorMessage: err?.message }));
          }),
        ),
      ),
    ),
  );

  updateLicense$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LicenseActions.UpdateLicense.request),
      exhaustMap(({ accountId, licenses }) => {
        const updates = licenses.map((license) => ({
          id: license.licenseCode,
          changes: { state: license.state },
        }));
        const pendingLicenses = licenses
          .filter((license) => license.state === LicenseState.Pending)
          .map((license) => license.licenseCode);
        return pendingLicenses.length
          ? [
              LicenseActions.UpdateLicense.success({ updates }),
              LicenseActions.UpdateLicense.status({
                accountId,
                licenses: pendingLicenses,
              }),
            ]
          : [LicenseActions.UpdateLicense.success({ updates })];
      }),
    ),
  );

  updateLicenseStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LicenseActions.UpdateLicense.status),
      exhaustMap(({ accountId, licenses }) =>
        merge(
          ...licenses.map((license) => {
            let stopRepeating = false;
            return this.accountService
              .getAccountLicenseState(accountId, license)
              .pipe(
                mergeMap((checkingLicense) => {
                  const { state } = checkingLicense as any;
                  stopRepeating = state !== LicenseState.Pending;
                  return stopRepeating ? of(checkingLicense) : EMPTY;
                }),
                repeatWhen((checkingLicense) =>
                  checkingLicense.pipe(
                    takeWhile(() => !stopRepeating),
                    delay(1000),
                  ),
                ),
                map((response) => {
                  const { licenseCode, state } = response as any;
                  return LicenseActions.UpdateLicense.success({
                    updates: [{ id: licenseCode, changes: { state } }],
                  });
                }),
              );
          }),
        ),
      ),
    ),
  );

  removeLicense$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LicenseActions.RemoveLicense.request),
      mergeMap(({ accountId, licenseCode }) =>
        this.accountService.deleteAccountLicense(accountId, licenseCode).pipe(
          map(() => LicenseActions.RemoveLicense.success({ licenseCode })),
          catchError((err: HttpErrorResponse) => {
            this.toastrService.error(err?.message);
            return of(LicenseActions.failure({ errorMessage: err?.message }));
          }),
        ),
      ),
    ),
  );
}
